import { useState, useRef, useCallback, InputHTMLAttributes } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export interface UseEditableTitleProps {
  initialValue: string;
  recordId: string;
  onSuccess?: (newValue: string) => void;
  onError?: (error: string) => void;
  onEditStart?: () => void;
  onEditEnd?: () => void;
  validation?: {
    minLength?: number;
    maxLength?: number;
    pattern?: RegExp;
  };
}

export interface UseEditableTitleReturn {
  // State
  isEditing: boolean;
  isSaving: boolean;
  currentValue: string;
  error: string | null;
  
  // Actions
  startEditing: () => void;
  cancelEditing: () => void;
  saveValue: (value: string) => Promise<void>;
  
  // Input props
  inputRef: React.RefObject<HTMLInputElement>;
  inputProps: InputHTMLAttributes<HTMLInputElement>;
}

export const useEditableTitle = ({
  initialValue,
  recordId,
  onSuccess,
  onError,
  onEditStart,
  onEditEnd,
  validation = {},
}: UseEditableTitleProps): UseEditableTitleReturn => {
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [currentValue, setCurrentValue] = useState(initialValue);
  const [error, setError] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState(initialValue);
  
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const validateValue = (value: string): string | null => {
    const trimmedValue = value.trim();
    
    if (!trimmedValue) {
      return "Название не может быть пустым";
    }
    
    if (validation.minLength && trimmedValue.length < validation.minLength) {
      return `Название должно содержать не менее ${validation.minLength} символов`;
    }
    
    if (validation.maxLength && trimmedValue.length > validation.maxLength) {
      return `Название должно содержать не более ${validation.maxLength} символов`;
    }
    
    if (validation.pattern && !validation.pattern.test(trimmedValue)) {
      return "Название содержит недопустимые символы";
    }
    
    return null;
  };

  const startEditing = useCallback(() => {
    setIsEditing(true);
    setError(null);
    setTempValue(currentValue);
    onEditStart?.();
    
    // Focus input after state update
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [currentValue, onEditStart]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setError(null);
    setTempValue(currentValue);
    onEditEnd?.();
  }, [currentValue, onEditEnd]);

  const saveValue = useCallback(async (value: string) => {
    const trimmedValue = value.trim();
    
    // Don't save if value hasn't changed
    if (trimmedValue === currentValue) {
      setIsEditing(false);
      onEditEnd?.();
      return;
    }
    
    // Validate
    const validationError = validateValue(value);
    if (validationError) {
      setError(validationError);
      return;
    }
    
    setError(null);
    setIsSaving(true);
    
    try {
      const { error: updateError } = await supabase
        .from("records")
        .update({ name: trimmedValue })
        .eq("id", recordId)
        .single();
      
      if (updateError) {
        throw updateError;
      }
      
      setCurrentValue(trimmedValue);
      setTempValue(trimmedValue);
      setIsEditing(false);
      onEditEnd?.();
      
      onSuccess?.(trimmedValue);
      toast({
        title: "Успешно",
        description: "Название записи обновлено",
      });
      
    } catch (err: unknown) {
      const errorMessage = `Ошибка сохранения: ${err.message || "Неизвестная ошибка"}`;
      setError(errorMessage);
      onError?.(errorMessage);
      toast({
        variant: "destructive",
        title: "Ошибка",
        description: errorMessage,
      });
    } finally {
      setIsSaving(false);
    }
  }, [currentValue, recordId, validation, onSuccess, onError, onEditEnd, toast]);

  // Можно использовать для автосохранения при изменении
  // const debouncedSave = useCallback(
  //   debounce((value: string) => {
  //     saveValue(value);
  //   }, 300),
  //   [saveValue]
  // );

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setTempValue(e.target.value);
    setError(null);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveValue(tempValue);
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditing();
    }
  }, [tempValue, saveValue, cancelEditing]);

  const handleBlur = useCallback(() => {
    if (!isSaving) {
      saveValue(tempValue);
    }
  }, [tempValue, saveValue, isSaving]);

  const inputProps: InputHTMLAttributes<HTMLInputElement> = {
    value: tempValue,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    autoFocus: isEditing,
    "aria-invalid": !!error,
    "aria-describedby": error ? "title-error" : undefined,
  };

  return {
    isEditing,
    isSaving,
    currentValue,
    error,
    startEditing,
    cancelEditing,
    saveValue,
    inputRef,
    inputProps,
  };
};